import { useMemo } from 'react';

import { Pagination } from '@sorare/core/src/atoms/navigation/Pagination';
import { useBackendPagination } from '@sorare/core/src/contexts/graphql-search';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';

import type { BackendCardResultsProps } from '#src/searchCards/AdvancedCardSearch/types.ts';
import { CardPreviewGrid } from '#src/searchCards/CardPreviewGrid/index.tsx';
import { CARDS_QUERY } from '#src/searchCards/CardResultsFromGraphQL/index.tsx';

export const BackendCardResultsFromGraphQL = ({
  hitSlugs,
  searchLoading,
  nbHits,
  hideOwner,
  galleryOwnerSlug,
  hideSorareUser,
  topic,
  showDesktopFilter,
  onlyPrimary,
  editableLists,
  sport,
  selectionMode,
  setSelectionMode,
  totalXpNeeded,
  canDrop,
  canLevelUp,
  hideScoutShortcuts,
  onToggleCard,
  PrependedItemComponent,
  selectedCardSlugs,
  showActionButtonOnFirstCard,
}: BackendCardResultsProps) => {
  const { isIosApp, isAndroidApp, googlePlayStoreCountryCode } =
    useIsMobileApp();
  const { page, pages, refine } = useBackendPagination();

  const { data, loading: dataLoading } = useQuery(
    CARDS_QUERY,
    {
      variables: {
        slugs: hitSlugs,
        editableLists: Boolean(editableLists),
        onlyPrimary: Boolean(onlyPrimary),
        isIosApp,
        isAndroidApp,
        googlePlayStoreCountryCode,
      },
      skip: searchLoading || hitSlugs.length === 0,
    },
    { skipCacheAndNetworkLoadingOverride: true }
  );

  const items = useMemo(() => {
    if (!data?.anyCards) {
      return [];
    }
    const cardsBySlug = new Map(data?.anyCards.map(c => [c.slug, c] as const));
    return hitSlugs.map(slug => cardsBySlug.get(slug)).filter(Boolean);
  }, [data?.anyCards, hitSlugs]);

  return (
    <>
      <CardPreviewGrid
        items={items}
        isBackend
        hideOwner={hideOwner}
        galleryOwnerSlug={galleryOwnerSlug}
        topic={topic}
        hideSorareUser={hideSorareUser}
        loading={searchLoading || dataLoading}
        showDesktopFilter={showDesktopFilter}
        editableLists={editableLists}
        onlyPrimary={!!onlyPrimary}
        sport={sport}
        selectionMode={selectionMode}
        setSelectionMode={setSelectionMode}
        totalXpNeeded={totalXpNeeded}
        hideScoutShortcuts={hideScoutShortcuts}
        canDrop={canDrop}
        canLevelUp={canLevelUp}
        onToggleCard={onToggleCard}
        selectedCardSlugs={selectedCardSlugs}
        showActionButtonOnFirstCard={showActionButtonOnFirstCard}
        prependedItem={
          PrependedItemComponent ? (
            <PrependedItemComponent total={nbHits} />
          ) : undefined
        }
        pagination={
          <Pagination currentPage={page - 1} pages={pages} onSelect={refine} />
        }
      />
    </>
  );
};
